//
//
//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ActivityEnum, AlertLevelEnum, RoleEnum, WalletEnum } from '@shared/Enums'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { dispatchError, OtDispatch, OtStoreState } from '@react/redux/store'

import { alertActions } from '@react/redux/alertSlice'
import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'

import { chainActions, ChainState, ChainStateInitialized } from '@react/redux/chainSlice'

import { Contract, ethers, JsonRpcProvider, Network, Signer } from 'ethers'

import { Erc20Abi, LYTPoolServiceConfigurationAbi, ServiceConfigurationAbi } from '@shared/Abis'

import {
  fetchPoolAccountAccessPairRegenerationLocalHostOnly,
  fetchPoolAccountAccessPairs,
  fetchPoolAccountsList,
} from '@react/fetchers/PoolFetchers'

import { fetchCompanyProfiles } from '@react/fetchers/companyProfileFetchers'
import { poolActions } from '@react/redux/poolSlice'

import { ChainConfiguration, UserConnection } from '@shared/DeploymentConfigTypes'
import { ChainAccountValues } from '@shared/LiquidityAsset'
import { wsServerActionAccountSubscribeAction } from '@shared/WsServerActions'
import { getRequiredAddress, otAssert } from '@shared/conversions'
import { PerimeterChainAddressesInterface } from '@shared/jest.shared.helper'
import { PoolBaseOverviewCTO, PoolLytOverviewCTO } from '@shared/sharedTypes'
import { ServiceConfigurationV5 } from '@typechain-types/contracts'
import { User } from 'oidc-client-ts'

export const LOCAL_COGNITO_USER: Partial<User> = {
  access_token: 'Local User',
}

export const loginToChainThunk =
  (
    signer: Signer,
    walletEnum: WalletEnum,
    roleEnumDefault: RoleEnum
  ): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    logOT.info('loginToChainThunk start loginToChainFunction', {
      walletEnum,
      roleEnumDefault,
    })
    await loginToChainFunction(
      dispatch,
      getState().poolState.poolOverviewCTOs,
      getState().chainState,
      signer,
      walletEnum,
      roleEnumDefault
    )
  }
export async function loginToChainFunction(
  dispatch: ThunkDispatch<OtStoreState, unknown, UnknownAction>,
  poolOverviewCTOs: Record<EthAddr, PoolBaseOverviewCTO>,
  chainState: ChainState,
  signer: Signer,
  walletEnum: WalletEnum,
  roleEnumDefault: RoleEnum
): Promise<void> {
  const activity = ActivityEnum.LOGIN
  try {
    logOT.info(loginToChainFunction.name, ' start', {
      walletEnum,
      roleEnumDefault,
    })
    dispatch(chainActions.loginStart())
    const { chainConfiguration, stageConfiguration, cognitoUser, webSocket } = chainState

    const serverApiBaseUrl = stageConfiguration.serverApiBaseUrl
    const providerReadOnly = chainConfiguration.providerReadOnly as JsonRpcProvider
    const restAPIKey = stageConfiguration.restAPIKey
    const chainConfigurationName = chainConfiguration.chainConfigurationName
    const perimeterAddresses = chainConfiguration.perimeterAddresses
    const accountAddr: EthAddr = getRequiredAddress(await signer.getAddress())
    logOT.info(loginToChainFunction.name, { accountAddr })
    otAssert(serverApiBaseUrl, 'serverApiBaseUrl null')
    otAssert(perimeterAddresses, 'perimeterAddresses null')
    otAssert(chainConfigurationName, 'chainConfigurationName null')
    localStorage.setItem('walletType', walletEnum)

    const accountBalances: ChainAccountValues = {}
    for (const liquidityAsset of perimeterAddresses.liquidityAssets) {
      const tokenContract = new ethers.Contract(liquidityAsset.addr, Erc20Abi, providerReadOnly)
      const balance = await tokenContract.balanceOf(accountAddr)
      accountBalances[liquidityAsset.addr] = balance.toString()
    }

    const roleEnum =
      roleEnumDefault === undefined || roleEnumDefault === RoleEnum.TO_BE_DETERMINED
        ? await determineRoleFromAccountAddr(
            perimeterAddresses,
            providerReadOnly,
            accountAddr,
            poolOverviewCTOs,
            stageConfiguration.isLyt,
            serverApiBaseUrl,
            restAPIKey
          )
        : roleEnumDefault

    if (stageConfiguration.stageName === 'local') {
      await fetchPoolAccountAccessPairRegenerationLocalHostOnly(serverApiBaseUrl, restAPIKey)
    }
    const canGetCompanyProfiles =
      roleEnum === RoleEnum.POOL_ADMIN ||
      roleEnum === RoleEnum.PROTOCOL_MANAGER ||
      roleEnum === RoleEnum.ISSUER ||
      roleEnum === RoleEnum.ISSUER_VAULT_WALLET ||
      roleEnum === RoleEnum.OPERATOR ||
      roleEnum === RoleEnum.BORROWER

    logOT.info(loginToChainFunction.name, {
      roleEnum,
      canGetCompanyProfiles,
      cognitoUser: !!cognitoUser,
      accountAddr,
    })
    const [poolAccounts, poolAccountAccessPairs, companyProfiles] = await Promise.all([
      fetchPoolAccountsList(serverApiBaseUrl, restAPIKey, accountAddr, chainConfigurationName),
      fetchPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey, accountAddr),

      cognitoUser && canGetCompanyProfiles
        ? fetchCompanyProfiles(serverApiBaseUrl, restAPIKey, cognitoUser).catch((error) => {
            logOT.error('fetchCompanyProfiles failed, continuing with empty profiles', error)
            return []
          })
        : [],
    ])
    dispatch(poolActions.setCompanyProfiles(companyProfiles))

    webSocket?.send(wsServerActionAccountSubscribeAction(accountAddr, chainConfigurationName))

    logOT.info(loginToChainFunction.name, { poolAccounts })
    const signedMessage = undefined

    const companyProfile = companyProfiles.find((cp) => cp.wallets.includes(accountAddr))
    dispatch(poolActions.setPoolAccountCTOList(poolAccounts))
    dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs }))
    const userConnection: UserConnection = {
      accountAddr,
      walletEnum,
      signer,
      roleEnum,
      companyProfile,
      accountBalances,
      signedMessage,
    }
    await dispatch(chainActions.loginUser(userConnection))
    await dispatch(alertActions.clearAlerts())

    logOT.info('loginToChainThunk poolAccounts', poolAccounts)
  } catch (e: any) {
    logOT.error('loginToChainThunk error', e)
    dispatch(
      alertActions.addAlert({
        title: 'Could Not Login',
        description: 'Could Not Login',
        createdAt: Date.now() / 1000,
        dueDateTime: Date.now() / 1000,
        txHash: '',
        sourceObjectAddr: ETH_ADDR_ZERO,
        alertLevel: AlertLevelEnum.ERROR,
      })
    )
    await dispatchError(dispatch, e, activity, 1057)
  }
}

export const loginDirectToChainThunk =
  (userConnection: UserConnection): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    logOT.info('loginDirectToChainThunk', userConnection)
    const activity = ActivityEnum.LOGIN
    try {
      dispatch(chainActions.loginStart())
      const { stageConfiguration, chainConfiguration, webSocket } = getState().chainState

      const serverApiBaseUrl = stageConfiguration.serverApiBaseUrl
      const restAPIKey = stageConfiguration.restAPIKey
      const chainConfigurationName = chainConfiguration.chainConfigurationName
      const providerReadUrl = chainConfiguration.frontendProviderUrl
      const perimeterAddresses = chainConfiguration.perimeterAddresses
      otAssert(perimeterAddresses, 'perimeterAddresses null')
      const provider = new ethers.JsonRpcProvider(providerReadUrl)

      let signer: Signer
      if (userConnection.walletEnum === WalletEnum.PRIVATE_KEY_STRING) {
        signer = new ethers.Wallet(userConnection.signerKey as string, new ethers.JsonRpcProvider(providerReadUrl))
      } else if (userConnection.walletEnum === WalletEnum.IMPERSONATE_ACCOUNT) {
        await provider.send('anvil_impersonateAccount', [userConnection.accountAddr])
        signer = await provider.getSigner(userConnection.accountAddr)
      } else {
        throw new Error('Invalid wallet enum')
      }

      const accountAddr: EthAddr = getRequiredAddress(await signer.getAddress())
      const walletEnum = userConnection.walletEnum

      localStorage.setItem('walletType', walletEnum)
      const roleEnum = userConnection.roleEnum
      const canGetCompanyProfiles =
        roleEnum === RoleEnum.POOL_ADMIN ||
        roleEnum === RoleEnum.PROTOCOL_MANAGER ||
        roleEnum === RoleEnum.ISSUER ||
        roleEnum === RoleEnum.ISSUER_VAULT_WALLET ||
        roleEnum === RoleEnum.OPERATOR ||
        roleEnum === RoleEnum.BORROWER

      const companyProfiles = canGetCompanyProfiles
        ? await fetchCompanyProfiles(serverApiBaseUrl, restAPIKey, LOCAL_COGNITO_USER as User)
        : []
      const companyProfile = companyProfiles.find((cp) => cp.wallets.includes(accountAddr))

      logOT.debug('loginToChainThunk companyProfile', companyProfile)

      const accountBalances: ChainAccountValues = {}
      for (const liquidityAsset of perimeterAddresses.liquidityAssets) {
        const tokenContract = new ethers.Contract(liquidityAsset.addr, Erc20Abi, provider)
        const balance = await tokenContract.balanceOf(accountAddr)

        accountBalances[liquidityAsset.addr] = balance.toString()
      }

      const newUserConnection: UserConnection = {
        accountAddr,
        walletEnum,
        signer,
        roleEnum,
        companyProfile,
        accountBalances,
      }
      dispatch(alertActions.clearAlerts())
      if (
        roleEnum === RoleEnum.POOL_ADMIN ||
        roleEnum === RoleEnum.PROTOCOL_MANAGER ||
        roleEnum === RoleEnum.ISSUER ||
        roleEnum === RoleEnum.ISSUER_VAULT_WALLET ||
        roleEnum === RoleEnum.OPERATOR ||
        roleEnum === RoleEnum.BORROWER
      ) {
        dispatch(chainActions.odicConnect(LOCAL_COGNITO_USER as User))
      }
      dispatch(chainActions.loginUser(newUserConnection))
      if (stageConfiguration.stageName === 'local') {
        await fetchPoolAccountAccessPairRegenerationLocalHostOnly(serverApiBaseUrl, restAPIKey)
      }
      const poolAccountAccessPairs = await fetchPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey, accountAddr)
      dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs }))

      dispatch(poolActions.setCompanyProfiles(companyProfiles))

      webSocket?.send(wsServerActionAccountSubscribeAction(accountAddr, chainConfigurationName))

      const poolAccounts = await fetchPoolAccountsList(
        serverApiBaseUrl,
        restAPIKey,
        accountAddr,
        chainConfigurationName
      )
      logOT.info('loginToChainThunk poolAccounts', poolAccounts)
      dispatch(poolActions.setPoolAccountCTOList(poolAccounts))

      logOT.debug('loginToChainThunk Sent on websocket', !!webSocket, roleEnum, accountAddr, chainConfigurationName)
    } catch (e: any) {
      logOT.error('loginToChainThunk error', e)
      dispatch(
        alertActions.addAlert({
          title: 'Could Not Login',
          description: 'Could Not Login',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1057)
    }
  }

async function isOperator(
  perimeterAddresses: PerimeterChainAddressesInterface,
  providerReadOnly: JsonRpcProvider,
  accountAddr: EthAddr
): Promise<boolean> {
  const serviceConfiguration = new Contract(
    perimeterAddresses.ServiceConfiguration,
    ServiceConfigurationAbi,
    providerReadOnly
  ) as unknown as ServiceConfigurationV5
  const operatorRole = await serviceConfiguration.OPERATOR_ROLE()

  const isOpp = await serviceConfiguration.hasRole(operatorRole, accountAddr)

  return isOpp
}
async function isPoolAdminVerified(
  perimeterAddresses: PerimeterChainAddressesInterface,
  providerReadOnly: JsonRpcProvider,
  accountAddr: EthAddr
): Promise<boolean> {
  const serviceConfiguration = new Contract(
    perimeterAddresses.ServiceConfiguration,
    ServiceConfigurationAbi,
    providerReadOnly
  )
  const poolAdminRole = await serviceConfiguration.POOL_ADMIN_ROLE()

  const isPoolAdmin = await serviceConfiguration.hasRole(poolAdminRole, accountAddr)

  return isPoolAdmin
}

async function isLytProtocolManager(
  perimeterAddresses: PerimeterChainAddressesInterface,
  providerReadOnly: JsonRpcProvider,
  accountAddr: EthAddr
): Promise<boolean> {
  const serviceConfiguration = new Contract(
    perimeterAddresses.ServiceConfiguration,
    LYTPoolServiceConfigurationAbi,
    providerReadOnly
  )
  const poolAdminRole = await serviceConfiguration.PROTOCOL_ADMIN()

  const isPoolAdmin = await serviceConfiguration.hasRole(poolAdminRole, accountAddr)

  return isPoolAdmin
}

function _addSignerToConfiguration(userConnection: UserConnection, chainConfiguration: ChainConfiguration): void {
  userConnection.signer = new ethers.Wallet(
    userConnection.signerKey as string,
    new ethers.JsonRpcProvider(
      chainConfiguration.frontendProviderUrl,
      new Network(chainConfiguration.chainName, chainConfiguration.chainId),
      {
        staticNetwork: true,
        pollingInterval: 100,
        polling: false,
        batchMaxCount: 1,
      }
    )
  )
}

const determineRoleFromAccountAddr = async (
  perimeterAddresses: PerimeterChainAddressesInterface,
  providerReadOnly: JsonRpcProvider,
  accountAddr: EthAddr,
  pools: Record<EthAddr, PoolBaseOverviewCTO>,
  isLyt: boolean,
  serverApiBaseUrl: string,
  restAPIKey: string
): Promise<RoleEnum> => {
  const poolValues = Object.values(pools)
  // if (poolValues.length === 0) {
  logOT.info('  PerimeterAddresses.PoolAccessControl', accountAddr)
  if (isLyt) {
    const isProtocolAdmin = await isLytProtocolManager(perimeterAddresses, providerReadOnly, accountAddr)
    logOT.info('  PerimeterAddresses.PoolAccessControl isProtocolAdmin', isProtocolAdmin, accountAddr)
    if (isProtocolAdmin) {
      return RoleEnum.PROTOCOL_MANAGER
    }

    for (let i = 0; i < poolValues.length; i++) {
      const pool = poolValues[i] as unknown as PoolLytOverviewCTO
      logOT.info('pool', pool)
      if (pool.borrowerManagerAddr === accountAddr) {
        logOT.info('IS ISSUER')
        return RoleEnum.ISSUER
      }
      if (pool.borrowerWalletAddr === accountAddr) {
        logOT.info('IS ISSUER VAULT WALLET')
        return RoleEnum.ISSUER_VAULT_WALLET
      }

      const poolAccountPairs = await fetchPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey, accountAddr)
      const isAllowed = poolAccountPairs.some((pair) => pair.poolAddr.toLowerCase() === pool.poolAddr.toLowerCase())
      if (isAllowed) {
        return RoleEnum.MARKET_MAKER
      }
    }
    return RoleEnum.LENDER
  }
  try {
    const isOpp = await isOperator(perimeterAddresses, providerReadOnly, accountAddr)
    if (isOpp) {
      return RoleEnum.OPERATOR
    }
    const isPoolAdmin = await isPoolAdminVerified(perimeterAddresses, providerReadOnly, accountAddr)
    logOT.info('  PerimeterAddresses.PoolAccessControl isPoolAdmin', isPoolAdmin, accountAddr)
    if (isPoolAdmin) {
      if (isLyt) {
        return RoleEnum.PROTOCOL_MANAGER
      }
      return RoleEnum.POOL_ADMIN
    }
  } catch (e) {
    logOT.error('  PerimeterAddresses.PoolAccessControl', accountAddr, e)
  }
  // }

  for (let i = 0; i < poolValues.length; i++) {
    const pool = poolValues[i]
    if (pool.borrowerManagerAddr === accountAddr) {
      return RoleEnum.BORROWER
    }
    if (pool.poolAdminAddr === accountAddr) {
      return RoleEnum.POOL_ADMIN
    }
  }
  return RoleEnum.LENDER
}
export async function fetchCompanyProfilesOnOdicLogin(
  chainState: ChainStateInitialized,
  dispatch: OtDispatch,
  cognitoUser: User
): Promise<void> {
  logOT.info(fetchCompanyProfilesOnOdicLogin.name, 'start', {
    cognitoUser: !!cognitoUser,
  })
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
  } = chainState

  logOT.info(fetchCompanyProfilesOnOdicLogin.name, {
    cognitoUser: !!cognitoUser,
  })
  if (cognitoUser) {
    const companyProfiles = await fetchCompanyProfiles(serverApiBaseUrl, restAPIKey, cognitoUser)
    logOT.info(fetchCompanyProfilesOnOdicLogin.name, { companyProfiles })
    dispatch(poolActions.setCompanyProfiles(companyProfiles))
  }
}
